import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { Navbar } from "@/components/landing/Navbar";
import { Hero } from "@/components/landing/Hero";
import { Ribbon } from "@/components/landing/Ribbon";
import { Collections } from "@/components/landing/Collections";
import { About } from "@/components/landing/About";
import { Features } from "@/components/landing/Features";
import { InstagramSection } from "@/components/landing/InstagramSection";
import { Contact } from "@/components/landing/Contact";
import { Footer } from "@/components/landing/Footer";
import { FloatingWhatsApp } from "@/components/landing/FloatingWhatsApp";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { BRAND } from "@/lib/brand";

const SITE_URL = "https://eliabejeans.com";
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

const setMeta = (name, content, isProperty = false) => {
  if (!content) return;
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href, extraAttrs = {}) => {
  if (!href) return;
  const selector = extraAttrs.hreflang
    ? `link[rel="${rel}"][hreflang="${extraAttrs.hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  Object.entries(extraAttrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const setStructuredData = (data) => {
  const id = "eliabe-structured-data";
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const Landing = () => {
  const { t, i18n } = useTranslation();
  useScrollReveal();

  useEffect(() => {
    const title = t("meta.title");
    const description = t("meta.description");
    const lang = i18n.language || "pt-BR";

    if (title) document.title = title;

    // Core meta
    setMeta("description", description);
    setMeta("robots", "index, follow, max-image-preview:large");
    setMeta("author", BRAND.name);
    setMeta("theme-color", "#2A3563");

    // Open Graph
    setMeta("og:type", "website", true);
    setMeta("og:site_name", BRAND.name, true);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:url", SITE_URL, true);
    setMeta("og:image", OG_IMAGE, true);
    setMeta("og:image:alt", title, true);
    setMeta("og:locale", lang.replace("-", "_"), true);

    // Twitter Card
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", OG_IMAGE);

    // Canonical + hreflang alternates
    setLink("canonical", SITE_URL);
    setLink("alternate", SITE_URL, { hreflang: "pt-BR" });
    setLink("alternate", SITE_URL, { hreflang: "en" });
    setLink("alternate", SITE_URL, { hreflang: "es" });
    setLink("alternate", SITE_URL, { hreflang: "x-default" });

    // Structured data — ClothingStore (LocalBusiness variant)
    setStructuredData({
      "@context": "https://schema.org",
      "@type": "ClothingStore",
      name: BRAND.name,
      url: SITE_URL,
      logo: `${SITE_URL}/logo192.png`,
      image: OG_IMAGE,
      description,
      telephone: `+${BRAND.phoneIntl}`,
      email: BRAND.email,
      sameAs: [BRAND.instagramUrl],
      address: {
        "@type": "PostalAddress",
        addressCountry: "BR",
        addressRegion: "PE",
      },
      makesOffer: {
        "@type": "Offer",
        itemOffered: {
          "@type": "Product",
          name: "Jeans Eliabe",
          category: "Apparel > Jeans",
          description:
            "Jeans do bebê ao plus size — tamanhos 1 ao 64, qualidade premium.",
        },
      },
    });
  }, [t, i18n.language]);

  return (
    <div className="bg-[#F9F9F6] min-h-screen" data-testid="landing-page">
      <Navbar />
      <main>
        <Hero />
        <Ribbon />
        <Collections />
        <About />
        <Features />
        <InstagramSection />
        <Contact />
      </main>
      <Footer />
      <FloatingWhatsApp />
      <Toaster position="top-center" richColors />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
